// src/pages/Admin/Techniques/AddTechniqueModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import { FaSave, FaTimes, FaImage, FaBookOpen, FaPlus } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import {
  addTechnique,
  getAllTechniques,
} from "../../../services/techniquesServices";
import { getBeltsList } from "../../../services/beltServices";

const DEFAULT_IMG = "/img/no-image.jpg";

// Helpers ceintures (tri par id)
const beltId = (b) =>
  b?.id ??
  b?.belt_id ??
  b?.ordre ??
  b?.order ??
  b?.rank ??
  Number.MAX_SAFE_INTEGER;

const beltLabel = (b) =>
  (b &&
    (b.label ||
      b.nom ||
      b.name ||
      b.belt_name ||
      b.couleur ||
      b.color ||
      b.belt)) ||
  String(b || "");

export default function AddTechniqueModal({
  isOpen,
  onClose,
  refresh, // callback pour recharger la liste
  existingFamilies, // optionnel: passer la liste des familles depuis la page
}) {
  const [formData, setFormData] = useState({
    nom: "",
    famille: "",
    ceinture: "",
    image1_url: "",
    video_url: "",
    traduction: "",
    information: "",
    uv2: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Familles et Ceintures dynamiques
  const [families, setFamilies] = useState([]);
  const [belts, setBelts] = useState([]); // [{id,label}]

  // Sélecteur: famille existante OU nouvelle
  const [familyMode, setFamilyMode] = useState("existing"); // "existing" | "new"
  const [newFamily, setNewFamily] = useState("");

  // Prépare/Reset à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      nom: "",
      famille: "",
      ceinture: "",
      image1_url: "",
      video_url: "",
      traduction: "",
      information: "",
      uv2: "",
    });
    setImgError(false);
    setSubmitting(false);
    setFamilyMode("existing");
    setNewFamily("");
  }, [isOpen]);

  // Récup familles (depuis prop si fournie, sinon via getAllTechniques)
  const loadFamilies = useCallback(async () => {
    try {
      if (Array.isArray(existingFamilies) && existingFamilies.length > 0) {
        const sorted = [
          ...new Set(existingFamilies.map((f) => (f || "").trim())),
        ]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setFamilies(sorted);
        return;
      }
      const data = await getAllTechniques();
      const setF = new Set();
      (Array.isArray(data) ? data : []).forEach((t) => {
        const f = (t?.famille || "").trim();
        if (f) setF.add(f);
      });
      setFamilies(Array.from(setF).sort((a, b) => a.localeCompare(b)));
    } catch (e) {
      console.error("Erreur chargement familles:", e);
      setFamilies([]);
    }
  }, [existingFamilies]);

  // Récup ceintures
  const loadBelts = useCallback(async () => {
    try {
      const res = await getBeltsList();
      const items = Array.isArray(res)
        ? res
            .map((b) => ({ id: beltId(b), label: (beltLabel(b) || "").trim() }))
            .filter((b) => b.label)
            .sort((a, b) => Number(a.id) - Number(b.id))
        : [];
      setBelts(items);
    } catch (e) {
      console.error("Erreur chargement ceintures:", e);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadFamilies();
    loadBelts();
  }, [isOpen, loadFamilies, loadBelts]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (name === "image1_url") setImgError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error("Le nom est requis.");
      return;
    }

    setSubmitting(true);
    try {
      const chosenFamily =
        familyMode === "new"
          ? newFamily.trim()
          : (formData.famille || "").trim();

      const payload = {
        nom: formData.nom.trim(),
        famille: chosenFamily || null,
        ceinture: formData.ceinture?.trim() || null,
        image1_url:
          formData.image1_url?.trim() && !imgError
            ? formData.image1_url.trim()
            : DEFAULT_IMG,
        video_url: formData.video_url?.trim() || null,
        traduction: formData.traduction?.trim() || null,
        information: formData.information?.trim() || null,
        uv2: formData.uv2?.trim() || null,
      };

      await addTechnique(payload);
      toast.success("Technique créée avec succès");
      refresh?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création technique:", error);
      toast.error("Erreur lors de la création de la technique");
    } finally {
      setSubmitting(false);
    }
  };

  const imgSrc =
    imgError || !formData.image1_url?.trim()
      ? DEFAULT_IMG
      : formData.image1_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal wrapper (scrollable) */}
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[85vh] flex flex-col">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <div className="flex items-center justify-center gap-2 text-white">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
              <FaBookOpen />
            </span>
            <h3 className="text-lg sm:text-xl font-bold uppercase">
              Ajouter une technique
            </h3>
          </div>
          <p className="text-white/90 text-center text-xs mt-1">
            Renseignez les informations ci-dessous.
          </p>
        </div>

        {/* Body scrollable */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            {/* Colonne image / aperçu */}
            <div className="space-y-3">
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4 flex items-center justify-center min-h-[260px]">
                <img
                  src={imgSrc}
                  alt="Aperçu technique"
                  onError={() => setImgError(true)}
                  className="max-h-[220px] max-w-full object-contain rounded-lg shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de l’image (illustration principale)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <FaImage />
                  </span>
                  <input
                    type="text"
                    name="image1_url"
                    placeholder="https://…/image.jpg"
                    value={formData.image1_url}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Laissez vide pour utiliser l’image par défaut.
                </p>
              </div>
            </div>

            {/* Colonne formulaire */}
            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Famille: existante OU nouvelle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Famille (existante)
                  </label>
                  <select
                    name="famille"
                    value={formData.famille}
                    onChange={handleChange}
                    disabled={familyMode === "new"}
                    className={`w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                      familyMode === "new"
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">— Aucune —</option>
                    {families.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Liste triée par ordre alphabétique.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Nouvelle famille
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFamily}
                      onChange={(e) => setNewFamily(e.target.value)}
                      placeholder="Saisir une nouvelle famille"
                      disabled={familyMode === "existing"}
                      className={`w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                        familyMode === "existing"
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    />
                    <Button
                      type="button"
                      text={familyMode === "existing" ? "Créer" : "Existante"}
                      icon={FaPlus}
                      onClick={() =>
                        setFamilyMode((m) =>
                          m === "existing" ? "new" : "existing"
                        )
                      }
                      className="!rounded-xl gap-2"
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Basculer entre famille existante et nouvelle.
                  </p>
                </div>
              </div>

              {/* Ceinture (liste dynamique triée par id) */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Ceinture (optionnelle)
                </label>
                <select
                  name="ceinture"
                  value={formData.ceinture}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">— Aucune —</option>
                  {belts.map((b) => (
                    <option key={`${b.id}-${b.label}`} value={b.label}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Vidéo */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de la vidéo (optionnelle)
                </label>
                <input
                  type="url"
                  name="video_url"
                  placeholder="https://…"
                  value={formData.video_url}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Traduction */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Traduction (optionnelle)
                </label>
                <input
                  type="text"
                  name="traduction"
                  placeholder="Ex : Nom japonais / anglais…"
                  value={formData.traduction}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Information */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Informations (optionnelles)
                </label>
                <textarea
                  name="information"
                  rows={4}
                  placeholder="Détails, repères pédagogiques, variantes…"
                  value={formData.information}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* UV2 */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  UV2 (optionnel)
                </label>
                <textarea
                  name="uv2"
                  rows={4}
                  placeholder="Contenu pédagogique / points clés UV2…"
                  value={formData.uv2}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-6">
            <Button
              text="Fermer"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Envoi..." : "Créer la technique"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${
                submitting ? "opacity-60 pointer-events-none" : ""
              }`}
              disabled={submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
